/**
 * Money handling
 * ==============
 * Amounts cross the wire as decimal strings and are summed as integer minor
 * units (cents). JavaScript numbers cannot represent most decimal fractions
 * exactly — `0.1 + 0.2 !== 0.3` — and a pipeline total that is a cent out is a
 * pipeline total nobody trusts.
 *
 * Values are stored in Postgres as `numeric(18,2)`, so two decimal places is
 * the contract throughout.
 */

export const MONEY_SCALE = 2;
const SCALE_FACTOR = 100n;

/** Parses a decimal string or number into integer cents. */
export function toCents(value: string | number | null | undefined): bigint {
  if (value === null || value === undefined || value === '') return 0n;

  const text = typeof value === 'number' ? value.toFixed(MONEY_SCALE) : value.trim();
  const match = /^(-)?(\d+)(?:\.(\d+))?$/u.exec(text);
  if (!match) throw new RangeError(`"${value}" is not a decimal amount`);

  const [, sign, whole = '0', fraction = ''] = match;
  // Pad or truncate to exactly two places. Truncation (rather than rounding) is
  // safe because the database never holds more than two.
  const cents = `${fraction}00`.slice(0, MONEY_SCALE);
  const magnitude = BigInt(whole) * SCALE_FACTOR + BigInt(cents);
  return sign === '-' ? -magnitude : magnitude;
}

/** Renders integer cents back as a fixed-point decimal string. */
export function fromCents(cents: bigint): string {
  const negative = cents < 0n;
  const magnitude = negative ? -cents : cents;
  const whole = magnitude / SCALE_FACTOR;
  const fraction = (magnitude % SCALE_FACTOR).toString().padStart(MONEY_SCALE, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** Exact sum of decimal amounts. */
export function sumMoney(values: readonly (string | number | null | undefined)[]): string {
  return fromCents(values.reduce<bigint>((total, value) => total + toCents(value), 0n));
}

/**
 * `amount x probability / 100`, rounded half-up to the nearest cent.
 *
 * Integer arithmetic throughout: multiply first, then divide, so the rounding
 * happens exactly once at the end.
 */
export function weightedAmount(amount: string | number, probability: number): string {
  if (!Number.isFinite(probability)) throw new RangeError('Probability must be a number');
  const clamped = Math.max(0, Math.min(100, Math.round(probability)));
  const cents = toCents(amount);
  const scaled = cents * BigInt(clamped);
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  // +50 before dividing by 100 is half-up rounding in integer arithmetic.
  const rounded = (magnitude + 50n) / 100n;
  return fromCents(negative ? -rounded : rounded);
}

export interface ForecastableDeal {
  amount: string | number;
  probability: number;
  isWon?: boolean;
  isLost?: boolean;
}

export interface ForecastTotals {
  openCount: number;
  /** Face value of open deals. */
  openValue: string;
  /** Open value discounted by each deal's own probability. */
  weightedValue: string;
  wonValue: string;
  lostValue: string;
}

/**
 * The weighted forecast: every open deal contributes its amount discounted by
 * its own probability. Won and lost deals are reported separately rather than
 * being folded in — a closed-won deal is revenue, not a forecast, and mixing
 * the two is how a pipeline number quietly becomes meaningless.
 */
export function forecastTotals(deals: readonly ForecastableDeal[]): ForecastTotals {
  let openCount = 0;
  let openCents = 0n;
  let weightedCents = 0n;
  let wonCents = 0n;
  let lostCents = 0n;

  for (const deal of deals) {
    const cents = toCents(deal.amount);
    if (deal.isWon) {
      wonCents += cents;
    } else if (deal.isLost) {
      lostCents += cents;
    } else {
      openCount += 1;
      openCents += cents;
      weightedCents += toCents(weightedAmount(deal.amount, deal.probability));
    }
  }

  return {
    openCount,
    openValue: fromCents(openCents),
    weightedValue: fromCents(weightedCents),
    wonValue: fromCents(wonCents),
    lostValue: fromCents(lostCents),
  };
}

/** Win rate over decided deals, as a percentage. Undecided deals are ignored. */
export function winRate(wonCount: number, lostCount: number): number {
  const decided = wonCount + lostCount;
  if (decided === 0) return 0;
  return Math.round((wonCount / decided) * 1000) / 10;
}
